import { PropsWithChildren, useCallback, useMemo } from 'react';

import { UnScheduledEmployeePunch } from '@sbv-clients/data-access-api';
import {
  Button,
  DataGridPremium,
  GridColDef,
  GridRowIdGetter,
} from '@sbv-clients/ui-shared';

import { ArrivalsResponseType } from '../../../../services/dashboard';
import { OpenUnScheduleModalType } from '../unscheduled-modal';

interface ArrivalsTableProps {
  data: ArrivalsResponseType[];
  unScheduledArrivals: UnScheduledEmployeePunch[];
  openModal: OpenUnScheduleModalType;
  isLoading: boolean;
}

const TextButton = ({
  children,
  onClick,
}: {
  onClick: () => void;
} & PropsWithChildren) => {
  return (
    <Button size="small" variant="text" sx={{ p: 1 }} onClick={onClick}>
      {children || 0}
    </Button>
  );
};

const ArrivalsTable = ({
  data,
  unScheduledArrivals,
  openModal,
  isLoading,
}: ArrivalsTableProps) => {
  const columns: GridColDef<ArrivalsResponseType>[] = useMemo(() => {
    return [
      {
        field: 'startTime',
        headerName: 'Start Time',
        sortable: false,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value) =>
          value === 'Unscheduled' ? 'Unscheduled' : value,
      },
      {
        field: 'scheduled',
        headerName: 'Scheduled',
        sortable: false,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.row.startTime === 'Unscheduled') {
            return '-';
          }
          return (
            <TextButton
              onClick={() =>
                openModal('scheduled', params.row.scheduledEmployees as any)
              }
            >
              {params.row.scheduled || 0}
            </TextButton>
          );
        },
      },
      {
        field: 'in',
        headerName: 'In',
        sortable: false,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.row.startTime === 'Unscheduled') {
            return (
              <TextButton
                onClick={() =>
                  openModal('unscheduled-arrivals', unScheduledArrivals)
                }
              >
                {unScheduledArrivals.length}
              </TextButton>
            );
          }
          return (
            <TextButton
              onClick={() => openModal('in', params.row.inEmployees as any)}
            >
              {params.row.in || 0}
            </TextButton>
          );
        },
      },
      {
        field: 'difference',
        headerName: 'Difference',
        sortable: false,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.row.startTime === 'Unscheduled') {
            return '-';
          }
          return (
            <TextButton
              onClick={() =>
                openModal('difference', params.row.differenceEmployees as any)
              }
            >
              {params.row.difference || 0}
            </TextButton>
          );
        },
      },
      {
        field: 'late',
        headerName: 'Late',
        sortable: false,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.row.startTime === 'Unscheduled') {
            return '-';
          }
          return (
            <TextButton
              onClick={() => openModal('late', params.row.lateEmployees as any)}
            >
              {params.row.late || 0}
            </TextButton>
          );
        },
      },
    ] as GridColDef<ArrivalsResponseType>[];
  }, [openModal, unScheduledArrivals]);

  const getRowId: GridRowIdGetter<ArrivalsResponseType> = useCallback(
    (row) => row.startTime as string,
    []
  );

  return (
    <DataGridPremium
      rows={data}
      columns={columns}
      disableRowSelectionOnClick
      getRowId={getRowId}
      disableColumnMenu
      hideFooterPagination
      hideFooter
      autoHeight
      loading={isLoading}
    />
  );
};

export default ArrivalsTable;
